import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, FormControl, TextField, Typography, Checkbox, Tooltip } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import SimpleMultiSelectButtonsGroup from 'components/common/simpleMultiSelectButtonsGroup';
import { stateOptions } from 'constants/campaign-filters';
import { countryOptions } from 'constants/campaign-filters';

import M from 'messages';
import classes from '../styles';

const countriesAll = countryOptions.map(c => ({ id: c.Numeric, name: c.Country }));
const filter = createFilterOptions();
const getOptionLabel = option => option?.name || '';
const optionRenderer = (props, option, { selected }) => (
  <Box {...props} key={option.id}>
    <Checkbox
      color="primary"
      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
      checkedIcon={<CheckBoxIcon fontSize="small" />}
      style={{ marginRight: 8 }}
      checked={selected}
    />
    {getOptionLabel(option)}
  </Box>
);

const WineLoversFilters = ({ onChange, data, disabled = false }) => {
  const isUSASelected = Array.isArray(data.country) ? data.country.includes('United States of America') : false;

  useEffect(() => {
    if (!isUSASelected && Array.isArray(data.state) && data.state.length) {
      onChange('state', []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data.country || [])]);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Box display="flex" alignItems="center">
            <Typography sx={classes.fieldHeaderText} component="h1">
              {M.get('campaigns.view.interaction.label')}
            </Typography>
            {
              <Tooltip
                title={
                  <Typography
                    sx={{
                      fontSize: '11px',
                      lineHeight: 'normal',
                      color: 'inherit',
                    }}>
                    {M.get('campaigns.view.interaction.tooltip')}
                  </Typography>
                }
                placement="top">
                <HelpOutlineIcon sx={classes.helpIcon} />
              </Tooltip>
            }
          </Box>
          <SimpleMultiSelectButtonsGroup
            name="partnerAudience"
            options={['followers', 'rated', 'saved']}
            values={data.partnerAudience}
            handleChange={onChange}
            noTranslation={true}
            size="medium"
            disabled={disabled}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Box display="flex" alignItems="center">
            <Typography sx={classes.fieldHeaderText} component="h1">
              {M.get('campaigns.view.role.label')}
            </Typography>
            {
              <Tooltip
                title={
                  <Typography
                    sx={{
                      fontSize: '11px',
                      lineHeight: 'normal',
                      color: 'inherit',
                    }}>
                    {M.get('campaigns.view.role.tooltip')}
                  </Typography>
                }
                placement="top">
                <HelpOutlineIcon sx={classes.helpIcon} />
              </Tooltip>
            }
          </Box>
          <SimpleMultiSelectButtonsGroup
            name="role"
            options={[
              'Event Organizer',
              'Importer/Distributor',
              'Retailer/Wine Shop',
              'Wine Journalist/Educator',
              'Sommelier/Restaurant',
              'Wine Enthusiast',
              'Winery Professional',
              'Other',
              'notSet',
            ]}
            values={data.role}
            handleChange={onChange}
            noTranslation={true}
            size="medium"
            disabled={disabled}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Box display="flex" alignItems="center">
            <Typography sx={classes.fieldHeaderText} component="h1">
              {M.get('campaigns.view.tradeConsumerAudience.label')}
            </Typography>
            {
              <Tooltip
                title={
                  <Typography
                    sx={{
                      fontSize: '11px',
                      lineHeight: 'normal',
                      color: 'inherit',
                    }}>
                    {M.get('campaigns.view.tradeConsumerAudience.tooltip')}
                  </Typography>
                }
                placement="top">
                <HelpOutlineIcon sx={classes.helpIcon} />
              </Tooltip>
            }
          </Box>
          <SimpleMultiSelectButtonsGroup
            name="business"
            options={['Business', 'Individual', 'notSet']}
            values={data.business}
            handleChange={onChange}
            noTranslation={true}
            size="medium"
            disabled={disabled}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Box display="flex" alignItems="center">
            <Typography sx={classes.fieldHeaderText} component="h1">
              {M.get('campaigns.view.locationTimingFilters.label')}
            </Typography>
            {
              <Tooltip
                title={
                  <Typography
                    sx={{
                      fontSize: '11px',
                      lineHeight: 'normal',
                      color: 'inherit',
                    }}>
                    {M.get('campaigns.view.locationTimingFilters.tooltip')}
                  </Typography>
                }
                placement="top">
                <HelpOutlineIcon sx={classes.helpIcon} />
              </Tooltip>
            }
          </Box>
        </Grid>
        <Grid container size={{ xs: 12 }}>
          <Grid container size={{ xs: 12 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth variant="outlined">
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={countriesAll}
                  value={countriesAll.filter(c => (data.country || []).includes(c.name))}
                  getOptionLabel={getOptionLabel}
                  filterOptions={filter}
                  onChange={(_, list) =>
                    onChange(
                      'country',
                      list.map(i => i.name),
                    )
                  }
                  renderOption={optionRenderer}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={M.get('campaigns.filters.country.title')}
                      placeholder={M.get('campaigns.filters.country.title')}
                    />
                  )}
                  disabled={disabled}
                />
              </FormControl>
            </Grid>
            {isUSASelected && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth variant="outlined">
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={stateOptions.map(s => ({ id: s.value, name: s.label }))}
                    value={stateOptions
                      .map(s => ({ id: s.value, name: s.label }))
                      .filter(s => (data.state || []).includes(s.name))}
                    getOptionLabel={getOptionLabel}
                    filterOptions={filter}
                    onChange={(_, list) =>
                      onChange(
                        'state',
                        list.map(i => i.name),
                      )
                    }
                    renderOption={optionRenderer}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={params => <TextField {...params} label={'State'} placeholder={'State'} />}
                    disabled={disabled}
                  />
                </FormControl>
              </Grid>
            )}
          </Grid>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
                label="Registration Date From"
                value={data.regDate?.from ? dayjs(data.regDate.from) : null}
                onChange={e =>
                  onChange('regDate', { ...(data.regDate || {}), from: e ? e.format('YYYY-MM-DD') : null })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
                label="Registration Date To"
                value={data.regDate?.to ? dayjs(data.regDate.to) : null}
                onChange={e => onChange('regDate', { ...(data.regDate || {}), to: e ? e.format('YYYY-MM-DD') : null })}
              />
            </Grid>
          </LocalizationProvider>
        </Grid>
      </Grid>
    </Box>
  );
};

WineLoversFilters.propTypes = {
  wlFilters: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  isUserSuperAdmin: PropTypes.bool,
  showPartnerSelect: PropTypes.bool,
  data: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

export default WineLoversFilters;
